import { useState } from "react";
import { SyndicationFeed } from "api/syndicationFeed";
import { Modal, Show } from "shared/components";
import { SYNDICATION_FEED_LABEL } from "views/settings-integration/integrations/shared/constants";
import { ChooseListingsStep } from "views/settings-integration/components/AddIntegrationModal/ChooseListingsStep";
import { CredentialsStep } from "views/settings-integration/components/AddIntegrationModal/CredentialsStep";
import { FirstStep } from "views/settings-integration/components/AddIntegrationModal/FirstStep";
import { Header } from "views/settings-integration/components/AddIntegrationModal/Header";
import { NameStep } from "views/settings-integration/components/AddIntegrationModal/NameStep";
import { SecondStep } from "views/settings-integration/components/AddIntegrationModal/SecondStep";
import {
  IntegrationId,
  DIRECT_CONNECT_INTEGRATIONS,
  isPmsIntegration,
  Step,
  SYNDICATION_FEED_BY_INTEGRATION
} from "views/settings-integration/components/AddIntegrationModal/types";
import { PropertyManagementSystem } from "shared/types/partner";

interface AddIntegrationModalProps {
  opened: boolean;
  onClose: () => void;
  onConnectSuccess?: (integrationId: IntegrationId) => void;
}

const needsCredentialStep = (
  id: PropertyManagementSystem | IntegrationId
): boolean => isPmsIntegration(id);

const isSyndicationFeed = (id: PropertyManagementSystem | IntegrationId) =>
  Boolean(SYNDICATION_FEED_BY_INTEGRATION[id as IntegrationId]);

const getTotalSteps = (
  id: PropertyManagementSystem | IntegrationId
): number => {
  if (isSyndicationFeed(id)) return 2;
  if (DIRECT_CONNECT_INTEGRATIONS.has(id)) return 1;
  if (needsCredentialStep(id)) return 3;
  return 2;
};

export const AddIntegrationModal = ({
  opened,
  onClose,
  onConnectSuccess
}: AddIntegrationModalProps) => {
  const [selectedIntegration, setSelectedIntegration] = useState<
    PropertyManagementSystem | IntegrationId
  >("" as PropertyManagementSystem | IntegrationId);
  const [currentStep, setCurrentStep] = useState<Step>(Step.FIRST);
  const [credentials, setCredentials] = useState<
    Record<string, string | boolean>
  >({});

  const handleClose = () => {
    onClose();
    setSelectedIntegration("" as PropertyManagementSystem | IntegrationId);
    setCurrentStep(Step.FIRST);
    setCredentials({});
  };

  const totalSteps = getTotalSteps(selectedIntegration);

  const selectedFeed =
    SYNDICATION_FEED_BY_INTEGRATION[selectedIntegration as IntegrationId];

  const handleCredentialsSubmit = (creds: Record<string, string | boolean>) => {
    setCredentials(creds);
    setCurrentStep(Step.THIRD);
  };

  const isChooseListingsStep = currentStep === Step.CHOOSE_LISTINGS;

  const feedLabel = selectedFeed ? SYNDICATION_FEED_LABEL[selectedFeed] : "";

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      panelClassName={`${isChooseListingsStep ? "max-w-[640px]" : "max-w-[480px]"} w-full flex flex-col p-6 shadow-xl max-h-[90vh]`}
    >
      <Header
        currentStep={currentStep}
        totalSteps={totalSteps}
        feedLabel={feedLabel}
        onClose={isChooseListingsStep ? handleClose : undefined}
      />

      <Show if={currentStep === Step.FIRST}>
        <FirstStep
          selectedIntegration={selectedIntegration}
          setSelectedIntegration={setSelectedIntegration}
          setCurrentStep={setCurrentStep}
          closeModal={handleClose}
        />
      </Show>

      <Show
        if={
          currentStep === Step.SECOND &&
          needsCredentialStep(selectedIntegration)
        }
      >
        <CredentialsStep
          selectedIntegration={selectedIntegration}
          onCredentialsSubmit={handleCredentialsSubmit}
          setCurrentStep={setCurrentStep}
          credentials={credentials}
        />
      </Show>

      <Show
        if={currentStep === Step.THIRD && isPmsIntegration(selectedIntegration)}
      >
        <NameStep
          selectedIntegration={selectedIntegration}
          credentials={credentials}
          setCurrentStep={setCurrentStep}
          closeModal={handleClose}
        />
      </Show>

      <Show
        if={
          currentStep === Step.SECOND &&
          !needsCredentialStep(selectedIntegration)
        }
      >
        <SecondStep
          setCurrentStep={setCurrentStep}
          closeModal={handleClose}
          selectedIntegration={selectedIntegration}
          onConnectSuccess={onConnectSuccess}
        />
      </Show>

      <Show if={isChooseListingsStep && Boolean(selectedFeed)}>
        <ChooseListingsStep
          feed={selectedFeed as SyndicationFeed}
          feedLabel={feedLabel}
          closeModal={handleClose}
          onBack={() => setCurrentStep(Step.FIRST)}
        />
      </Show>
    </Modal>
  );
};
